import React, { FC, useEffect, useRef, useState } from 'react'
import { observable } from 'mobx'
import { observer, /* inject */ } from 'mobx-react'
import { t } from 'ttag'
import _debug from 'debug'

import './EnhancedResources.css'
import { LexMeaning, DefinitionPart, MarbleLemma, MarbleLemmas } from '../../scrRefs/Lemmas'
import { RefRange } from '../../scrRefs/RefRange'
import { Root, IRoot } from '../../models3/Root'
import { ProjectReference } from '../../models3/ProjectReferences'
import { convertReferenceStrings } from '../../resources/Versifications'
import { Passage, PassageGloss, PassageVideo, Project, ProjectTerm } from '../../models3/ProjectModels'
import { CancelButton, OKButton, PencilButton, PlayButton, RecordButton, StarButton } from '../utils/Buttons'
import { displayError } from '../utils/Errors'
import { fmt, s } from '../utils/Fmt'
import { IKeyTermRecording, KeyTermGlosses } from './KeyTermGlosses'
import { GlossReference } from '../../models3/GlossReferences'
import _ from 'underscore'

// Show the definition and references for the currently selected Biblical Term

const log = _debug('sltt:ERTermView')

type IIsKeyTermView = {
    rt: IRoot,
    term: ProjectTerm | null,
    meaning: LexMeaning,
}

export const IsKeyTermView: FC<IIsKeyTermView> = observer(({ rt, term, meaning }) => {
    let { project, iAmConsultant } = rt
    let isKeyTerm  = term?.isKeyTerm ?? false

    async function toggleIsKeyTerm() {
        if (term) {
            await term.setIsKeyTerm(!isKeyTerm)
            return
        }

        let _term = project.createProjectTerm(meaning.lexicalLink)
        _term.isKeyTerm = true
        await project.addProjectTerm(_term)
    }

    return (
        <StarButton
            isHollow={!isKeyTerm}
            enabled={iAmConsultant}
            onClick={toggleIsKeyTerm}
            className='er-star-icon'
            tooltip={isKeyTerm ? `Unset as key term` : `Set as key term`}
        />
    )
})

type IERMeaningView = {
    rt: Root,
    lemma: MarbleLemma,
    _meaning: LexMeaning, // LexMeaning corresponding to this view
    setTermIdAndMeaning: (termId: string, meaning:LexMeaning | null) => void,
    meaning: LexMeaning | null, // meaning selected by user, if any
    setProjectReference: (projectRefernce: ProjectReference | null) => void,
    setSignPlaying: (signVideo: PassageVideo) => void,
    termId: string, // For example, "SDBG:γένεσις:000001"
    setRecording: (recording: IKeyTermRecording | null) => void,
    playGlossReference: (reference: GlossReference) => void,
    setGlossShowing: (glossText: string) => void,
}

/**
 * Show information for one meaning, i.e. "sense" of selected MarbleTerm
 */
const ERMeaningView: FC<IERMeaningView> =
    observer(({ rt, lemma, _meaning, meaning, setTermIdAndMeaning, setProjectReference, termId, 
        setRecording, setSignPlaying, playGlossReference, setGlossShowing }) => 
{
    let { project, iAmDeveloper } = rt
    let meaningRef = useRef<HTMLDivElement>(null)
    let isSelected = (_meaning === meaning)
    let [passages, setPassages] = useState<Passage[]>([])

    let projectTerm = rt.project.termsMap.get(_meaning.lexicalLink) ?? null

    // When this meaning becomes selected, scroll it into view
    useEffect(() => {
        if (isSelected) {
            meaningRef.current?.scrollIntoView()
        }
    }, [isSelected])

    let senseName = _meaning.senseName(rt.transliterateLemmas)

    const comments = _meaning.getComments(rt.uiLanguage, rt.project)

    ///log('getPassages render', fmt({_meaning: _meaning.id, passages: passages.map(p => p.name)}))

    return (
        <div ref={meaningRef} className={isSelected ? 'er-highlighted-sense' : ''}>
            <div className="er-header">
                <div className="er-lemma-title">
                    { senseName }
                    {iAmDeveloper && _meaning.entryCode && ` (${_meaning.entryCode})`}
                </div>
                <div className="er-iskeyterm">
                    <IsKeyTermView {...{rt, term: projectTerm, meaning: _meaning}} />
                </div>
            </div>
            <div className="er-glosses">
                <span className="er-explanation">{_meaning.glosses(rt.uiLanguage) || '***'}</span>
            </div>
            { _meaning.splitDefinitionShort(rt.uiLanguage).map((definitionLink, key) => (
                <ERDefinitionLinkView {...{ key, definitionLink, setTermIdAndMeaning }} />
            )) }
            {comments && <div>[{comments}]</div>}
            <div className="er-term-glosses">
                <KeyTermGlosses {...{
                    label: /* translator: important */ t`Glosses`,
                    rt, 
                    term: projectTerm, 
                    meaning: _meaning, 
                    setSignPlaying,
                    setRecording,
                    setGlossShowing }} />
            </div>
            <ERReferences {...{ 
                rt, 
                references: _meaning.references, 
                setProjectReference, 
                projectTerm, 
                playGlossReference }} />
        </div>
    )
})

type IERTermView = {
    rt: Root,
    lemma: MarbleLemma,
    setProjectReference: (projectRefernce: ProjectReference | null) => void,
    setTermIdAndMeaning: (termId: string, meaning:LexMeaning | null) => void,
    meaning: LexMeaning | null,
    termId: string,
    setRecording: (recording: IKeyTermRecording | null) => void,
    setSignPlaying: (signVideo: PassageVideo) => void,
    playGlossReference: (reference: GlossReference) => void,
    setGlossShowing: (glossText: string) => void,
}

const ERTermView: FC<IERTermView> =
    ({ rt, lemma, setProjectReference, termId, setTermIdAndMeaning, setRecording, setSignPlaying, 
                playGlossReference, meaning, setGlossShowing }) => {
        // log("ERTermView render", fmt({termId, lemma: lemma.lemmaString, meaning: meaning?.id}))
        return (
            <div className="er-term-div">
                {lemma.meanings.map((_meaning, index) => (
                    <div key={_meaning.id} onClick={() => setTermIdAndMeaning(termId, _meaning)}>
                        {(index > 0) && <hr />}
                        <ERMeaningView {...{
                            lemma, _meaning,
                            key: index,
                            rt, 
                            setSignPlaying, 
                            meaning, 
                            setProjectReference, 
                            termId, 
                            setTermIdAndMeaning, 
                            playGlossReference, 
                            setRecording,
                            setGlossShowing
                        }} />
                    </div>
                    )
                )}
            </div>
        )
    }

type IERDefinitionLinkView = {
    definitionLink: DefinitionPart,
    setTermIdAndMeaning: (termId: string, meaning:LexMeaning | null) => void,
}

const ERDefinitionLinkView: FC<IERDefinitionLinkView> = ({ definitionLink, setTermIdAndMeaning }) => {
    let { text, link } = definitionLink
    if (!link) return (<span>{text}</span>)

    return (<span className="er-definition-link"
            onClick={(e) => {
                e.stopPropagation()
                const { termId, meaning } = MarbleLemmas.getByLink(link)
                if (termId) {
                    setTermIdAndMeaning(termId, meaning)
                }
            }}>
        {text}
    </span>)
}

type IERReferences = {
    rt: Root,
    references: string[],
    setProjectReference: (projectRefernce: ProjectReference | null) => void,
    projectTerm: ProjectTerm | null,
    playGlossReference: (reference: GlossReference) => void,
}

const ERReferences: FC<IERReferences> = ({ rt, references, setProjectReference, projectTerm, playGlossReference }) => {
    let newReferences = convertReferenceStrings(references, 'Original', rt.project.versification)
    let refRanges = RefRange.refsToRefRanges(newReferences)

    //  e.g. "Luke 1.3", "Acts 18.25-26",
    let displayParts = RefRange.refRangesToDisplayParts(refRanges)

    // log('ERReferences', fmt({references, newReferences, refRanges, displayParts}))

    let newBook = (i: number) => refRanges[i - 1].startRef.slice(0, 3) !== refRanges[i].startRef.slice(0, 3)

    return (
        <div className="er-references">
            {displayParts.map((part, index) => (
                <span key={index}>
                    {index > 0 && newBook(index) ? <br/> : (index > 0 ? '; ' : '')}
                    <ERReference
                        rt={rt}
                        key={index}
                        index={index}
                        refRange={refRanges[index]}
                        setProjectReference={setProjectReference}
                        projectTerm={projectTerm}
                        playGlossReference={playGlossReference}
                        part={part} />
                </span>
            ))}
        </div>
    )
}

type IERReference = { 
    rt: Root,
    part: string, 
    index: number,
    refRange: RefRange,
    setProjectReference: (projectRefernce: ProjectReference | null) => void,
    projectTerm: ProjectTerm | null,
    playGlossReference: (reference: GlossReference) => void,
}

const ERReference: FC<IERReference> = ({ rt, part, index, refRange, setProjectReference, projectTerm, playGlossReference }) => {
    let projectReferences = rt.projectReferences.get(refRange)
    
    if (projectReferences.length === 0) return (
        <span className="er-reference er-reference-link" key={index}>
            {part}
        </span>
    )

    return (
        <ERReferenceLink
            part={part}
            projectReferences={projectReferences}
            setProjectReference={setProjectReference}
            rt={rt}
            projectTerm={projectTerm} />
    )
}

type ERReferenceLinkProps = {
    part: string,
    projectReferences: ProjectReference[],
    setProjectReference: (projectRefernce: ProjectReference | null) => void,
    rt: Root,
    projectTerm: ProjectTerm | null,
}

const ERReferenceLink = observer((
        { part, projectReferences, setProjectReference, rt, projectTerm }: ERReferenceLinkProps) => 
{
    // log('ERReferenceLink', fmt({part, projectReferences: projectReferences.map(pr => pr.dbg(rt.project))}))

    projectReferences = _.sortBy(projectReferences, 'startingTime')
    
    return (
        <span className="er-reference">
            {projectReferences.map((projectReference, i) => {
                return (
                    <span key={i} className="er-reference-link er-reference-link-enabled"
                            onClick={() => setProjectReference(projectReference) } >
                        {i === 0 ? part : ''}
                        <PlayButton
                            enabled={true}
                            onClick={() => {}}
                            className='er-play-verse-button'
                            tooltip={projectReference.info(rt).tooltip}
                            selectionPresent={false} />
                    </span>
                )
            })}
        </span>
    )
})

// Find all glosses in this section of verses
function getGlossReferencesThatOverlapProjectReference(projectReference: ProjectReference, project: Project) {
    let glossReferences: GlossReference[] = []

    let { passageVideo } = projectReference
    let portion = project.portions.find(_portion => passageVideo._id.startsWith(_portion._id))
    let passage = portion?.passages.find(_passage => passageVideo._id.startsWith(_passage._id))

    if (!passage) {
        return []
    }

    let glosses = passageVideo.getVisibleGlosses(passage)
    glosses.forEach((gloss, index) => {
        const duration = gloss.durationByIndex(glosses, index)
        if (glossOverlapsProjectReference(gloss, projectReference, duration)) {
            glossReferences.push(new GlossReference(passage!, gloss, passageVideo))
        }
    })

    return glossReferences
}

function glossOverlapsProjectReference(gloss: PassageGloss, projectReference: ProjectReference, duration: number) {
    // either gloss is fully contained within the verse:
    //      verse.start <= gloss.start && verse.end >= gloss.end
    // OR gloss is partially contained within the verse:
    //      verse.start <= gloss.start && verse.end >= gloss.start
    //      || verse.start >= gloss.start && verse.start <= gloss.end
    let glossEnd = gloss.time + duration
    let isFullyContained = projectReference.startingTime <= gloss.time && projectReference.endingTime >= glossEnd
    let verseStartsBeforeGloss = projectReference.startingTime <= gloss.time && projectReference.endingTime >= gloss.time
    let verseStartsInGloss = projectReference.startingTime >= gloss.time && projectReference.startingTime <= glossEnd
    let isPartiallyContained = verseStartsBeforeGloss || verseStartsInGloss
    return isFullyContained || isPartiallyContained
}

export default ERTermView
